import type { UppyFile, Meta, Body } from "@uppy/core";

// The Upload row Rails returns from `complete`. It is a bare ActiveRecord
// `as_json`, so keys are snake_case. Phase 5 (the player) reads the same
// shape off `useUploadQueue` when a slot flips to "processing".
export interface UploadRecord {
  id: number;
  sha?: string;
  renditions_status?: string | null;
  processing_status?: ProcessingStatus;
  video?: VideoPayload | null;
  [key: string]: unknown;
}

// Media-agnostic readiness the serializer computes: images/documents are
// "ready" at create, video is derived from its rendition pipeline.
export type ProcessingStatus = "ready" | "processing" | "failed";

// One completed progressive (downloadable MP4) rendition. Ordered
// largest-first by the serializer.
export interface VideoRenditionPayload {
  preset: string;
  url: string;
  width: number | null;
  height: number | null;
}

// The raw uploaded file as a playback source, codec-gated by the serializer.
// `playable` is true only for a browser-safe progressive MP4 (h264 + aac/none
// in an mp4 container) — the first playable source, streamable via Range while
// the transcode ladder catches up.
export interface OriginalSourcePayload {
  url: string | null;
  playable: boolean;
}

// The `video` block the Upload serializer ships once media_type == "video".
export interface VideoPayload {
  hls_master_url: string | null;
  // True once ANY version is playable: a browser-compatible original, at least
  // one completed progressive rendition, or the HLS master. The player ungates
  // on this, not on `processing_status` ("ready" still means every rendition
  // settled).
  playable: boolean;
  poster_url: string | null;
  duration_seconds: number | null;
  original: OriginalSourcePayload;
  renditions: VideoRenditionPayload[];
}

// The lifecycle a queue slot walks through. `uploading` covers everything
// from the first signed part until the last byte streams; `finishing` covers
// the gap between 100% of bytes and a confirmed `complete` — a row must never
// read "Uploading — 100%"; `processing` is the hard line — the bytes are in
// R2 and the server owns the rest.
export type SlotStatus =
  | "pending" // queued, no parts in flight yet
  | "uploading" // parts actively transferring
  | "paused"
  | "finishing" // all bytes streamed; `complete` not yet confirmed
  | "processing" // upload complete; transcode/ingest is server-side
  | "error"
  | "cancelled";

// The immediate placeholder a caller of `enqueueUpload` gets back. It is a
// stable handle keyed by the Uppy file id; consumers render it straight away
// (upload pending → processing → ready) instead of blocking on the transfer.
export interface UploadSlot {
  id: string; // Uppy file id — stable for the file's lifetime
  name: string;
  size: number;
  status: SlotStatus;
  progress: number; // 0..100
  bytesUploaded: number;
  // Real throughput over a rolling window (bytes/sec), not instantaneous.
  bytesPerSecond: number | null;
  etaSeconds: number | null;
  errorMessage: string | null;
  // Present once `complete` succeeds — the ingested Upload row.
  upload: UploadRecord | null;
  // Server-generated multipart session id (our upload_session_id). Present
  // once createMultipartUpload has run; used only for diagnostics.
  uploadSessionId: string | null;
  // Tier 0: a preview frame captured locally from the picked File at enqueue
  // time (object URL), shown as the row thumbnail and placeholder poster before
  // any byte uploads. Best-effort — null when capture failed or wasn't a video.
  // Revoked when the slot is removed.
  previewUrl: string | null;
  // Whether an "error" slot can be retried. `false` only for slots with no
  // Uppy file behind them (a pick-time size rejection) — there is no transfer
  // to restart, so the row offers dismiss but not retry. Undefined means
  // retryable.
  retryable?: boolean;
}

export type UppyMeta = Meta;
export type UppyResponseBody = Body & { record?: UploadRecord };
export type QueueFile = UppyFile<UppyMeta, UppyResponseBody>;

// Per-type byte ceilings enforced at pick time, before an R2 multipart is
// opened. These MIRROR the server caps in `Upload` (app/models/upload.rb):
// MAX_MULTIPART_VIDEO_SIZE, MAX_IMAGE_SIZE, MAX_DOCUMENT_SIZE. The resumable
// path is always multipart, so video uses the 100GB resumable ceiling (not the
// 500MB single-PUT cap). Keep these in sync if the model constants change.
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024 * 1024; // 100 GB
export const MAX_IMAGE_BYTES = 100 * 1024 * 1024; // 100 MB
export const MAX_PDF_BYTES = 32 * 1024 * 1024; // 32 MB
// Anything that isn't video / image / pdf falls back to the image cap.
export const MAX_DEFAULT_BYTES = MAX_IMAGE_BYTES;

// The cap that applies to a given content type, plus a human label for the
// rejection message ("the limit for videos is 100 GB").
export function sizeLimitFor(contentType: string): { bytes: number; label: string } {
  const type = contentType || "";
  if (type.startsWith("video/")) return { bytes: MAX_VIDEO_BYTES, label: "videos" };
  if (type.startsWith("image/")) return { bytes: MAX_IMAGE_BYTES, label: "images" };
  if (type === "application/pdf") return { bytes: MAX_PDF_BYTES, label: "PDFs" };
  return { bytes: MAX_DEFAULT_BYTES, label: "this file type" };
}

// Human-readable byte size for user-facing copy: "150 GB", "32 MB", "900 KB".
// Whole numbers where clean, one decimal otherwise; not exact-power-of-two math.
export function formatFileSize(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 100 || Number.isInteger(value) ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}
